import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory } from 'react-router'
import { Typography, Button, Spinner } from '@telavita-core/react-design-kit'
import qs from 'querystring'
import clsx from 'clsx'

import axios from 'settings/axios'
import { apiEndpoints } from 'settings/_apiSettings'
import * as routes from 'settings/_routesSettings'

import { RequestTreatmentModal } from 'components'
import ProfessionalChip from 'components/ProfessionalChip'
import FloatActionButtonDropdown from 'components/FloatActionButtonDropdown/FloatActionButtonDropdown'

import * as actions from 'redux/actions'
import * as filterHomepageActions from 'redux/modules/filterHomepage/actions'
import { usePatientEligibility } from 'hooks/usePatientEligibility'

import { ReactComponent as SkipRightIcon } from 'static/svg/skip-right.svg'
import ArrowLeftIcon from 'components/CoreIcons/Icons/ArrowLeft'

import { ATTENDED, SCHEDULED } from 'settings/_scheduleStatusSettings'
import { PARTICULAR_PLAN_GROUP_CODE } from 'settings/_planGroupCode'
import { PATIENT_SAVED_PLAN } from 'settings/_storageFlagsSettings'
import { PlanGroupCategoryEnum, planGroupsSettings } from 'settings/_planGroupSettings'
import { updatePatientDefaultPlan } from 'services/updatePatientDefaultPlan'
import { AUTH_UPDATE_DEFAULT_PLAN } from 'redux/actions/_actionTypes'
import { getLastValidityDetail } from 'services/lastValidityDetail'
import { useEnablePsychiatry, useGetPatientReservations, usePatientSpecialities } from 'hooks'
import { BlockScheduleModal } from 'containers/ModalManager/Modals/BlockScheduleModal'

const {
  CORPORATE_LIMITED,
  CORPORATE_UNLIMITED,
  ENTERPRISE,
  CARE_PLUS,
  VITTA,
  CAPITATION
} = PlanGroupCategoryEnum

export const SpecialitiesSelector = ({
  onSuccess
}) => {

  const dispatch = useDispatch()
  const routerHistory = useHistory()

  const {
    patientPlans,
    handleRequestTreatementStatus,
    handleUpdateSelectedPlan
  } = usePatientEligibility()

  const username = useSelector((state) => state?.auth?.userData?.username)

  const { 
    quantityOfRecurrentReservations, 
    reservationProfessionalUsername 
  } = useGetPatientReservations(username)
  
  const patientHasReservation = quantityOfRecurrentReservations > 0
  const showPlanSelectTab = patientPlans.length > 1

  const patientPlansWithoutVitta = patientPlans.filter(pPlan => pPlan.plan_group.category !== VITTA)

  const [professionalHistory, setProfessionalHistory] = useState([])
  const [floatButtonTab, setFloatButtonTab] = useState(showPlanSelectTab ? 'SELECT_PLAN' : 'SELECT_SPECIALTY')
  const [professionalRole, setProfessionalRole] = useState()
  const [loadingHistory, setLoadingHistory] = useState(false)
  const [loadingEligibility, setLoadingEligibility] = useState(false)
  const [closeDropdown, setDropdown] = useState(false)
  const [requestTreatmentModalOpen, setRequestTreatmentModalOpen] = useState(false)
  const [blockScheduleModalOpen, setBlockScheduleModalOpen] = useState(false)
  const [planCode, setPlanCode] = useState(!showPlanSelectTab ? patientPlans[0].partner_plan_code : undefined)
  const [planGroupCode, setPlanGroupCode] = useState(PARTICULAR_PLAN_GROUP_CODE)

  const selectedPlan = planCode ? patientPlans.find(pPlan => pPlan.partner_plan_code === planCode) : undefined

  const isCassi = selectedPlan?.partner_plan_name === 'CASSI'
  const isCapitation = selectedPlan?.plan_group?.category === CAPITATION
  const isCareplus = [CARE_PLUS].includes(selectedPlan?.plan_group?.category)

  const daysToAuthorizeProcedure = planGroupsSettings[planGroupCode]?.daysToAuthorizeProcedure ?? 3

  const professionalType = professionalRole ?? 'PSIC'

  const { specialities, updatePlanGroupCode } = usePatientSpecialities(planGroupCode)
  const showPsychiatry = useEnablePsychiatry()

  const currentSpecialty = specialities.find(specialty => specialty.code === professionalType)

  const specialitiesOptions = specialities.map(speciality => ({
    label: <>Marcar com <b>{speciality.role}</b></>,
    professional_type: speciality.code,
    isLoading: loadingEligibility && speciality.code === professionalType 
  }))

  const plansOptions = patientPlansWithoutVitta.map(plan => ({
    planName: plan.partner_plan_name,
    planGroupName: plan.plan_group.name,
    planCode: plan.partner_plan_code,
    planGroupCode: plan.plan_group.code,
    category: plan.plan_group.category,
    isParticular: plan.partner_plan_is_particular,
    isLoading: loadingHistory && planCode === plan.partner_plan_code
  }))

  const orderedPlansOptions = plansOptions.sort((a, b) => {
    const aPlanGroupName = a.planGroupName.toLowerCase()
    const bplanGroupName = b.planGroupName.toLowerCase()

    if (a.isParticular && !b.isParticular) {
      return 1
    }
    if (!a.isParticular && b.isParticular) {
      return -1
    }
    if (aPlanGroupName < bplanGroupName) {
      return -1
    }
    if (aPlanGroupName > bplanGroupName) {
      return 1
    }
    return 0
  })

  const professionalsOptions = [
    ...professionalHistory.filter(professsional => professsional.profile_role === professionalType),
    { full_name: `outro ${showPsychiatry ? currentSpecialty.role : 'profissional'}`, professional_type: professionalType },
  ]

  const handleUpdateDefaultPlan = async (selectedPlanCode) => {
    const currentPlanCode = selectedPlanCode ? selectedPlanCode : planCode

    localStorage.setItem(PATIENT_SAVED_PLAN, currentPlanCode)
    handleUpdateSelectedPlan(currentPlanCode)
    dispatch({ type: AUTH_UPDATE_DEFAULT_PLAN, payload: { planCode: currentPlanCode } })
    await updatePatientDefaultPlan({
      planCode: currentPlanCode,
      username: username
    })
  }

  const handleProfessionalProfileRoute = async (professional, planCode) => {
    await handleUpdateDefaultPlan(planCode)
    const routeByProfileCode = {
      undefined: professional.professional_type === 'PSIC' ? routes.HOME_PSIC : routes.HOME_PSIQ,
      PSIC: routes.PROFESSIONAL_PSIC_PROFILE(professional.friendly_url),
      PSIQ: routes.PROFESSIONAL_PSIQ_PROFILE(professional.friendly_url),
    }
    dispatch(filterHomepageActions.clearPersons())
    dispatch(actions.clearPersons())
    routerHistory.push(routeByProfileCode[professional.profile_role])
  }

  const renderProfessionalChipButton = (professional) => {

    const professionalRole = professional?.profile_role

    const currentSpecialty = specialities.find(specialty => specialty.code === professionalRole)

    return (
      <ProfessionalChip
        name={professional.full_name}
        photo={professional.picture_url}
        professionalRole={currentSpecialty && currentSpecialty.role}
        customClassName='PatientHome__ProfessionalChip'
      />
    )
  }

  const renderSpecialtyButton = (data) => {
    return (
      <div className={clsx({
        'PatientHome__ChipWrapper': true,
        'PatientHome__ChipWrapper--loading': data.isLoading
      })}>
        {data.isLoading ? (
          <Spinner customClassName='PatientHome__ChipWrapper__Spinner'/>
        ) : (
          <>
            <Typography variant='content2'>
              {data.label}
            </Typography>
            <SkipRightIcon />
          </>
        )}
      </div>
    )
  }

  const renderPlanButton = (data) => {
    return (
      <div className={clsx({
        'PatientHome__ChipWrapper': true,
        'PatientHome__ChipWrapper--loading': data.isLoading
      })}>
        {data.isLoading ? (
          <Spinner customClassName='PatientHome__ChipWrapper__Spinner'/>
        ) : (
          <>
          <div className='PatientHome__ChipWrapper__TextWrapper'>
            {data.isParticular ? (
              <Typography variant='content2'>
                {data.planName}
              </Typography>
            ) : (
            <>
            <Typography variant='content2'>
              {data.planGroupName}
            </Typography>
            <Typography variant='content3' inlineStyles={{ color: '#898989' }}>
              {data.planName}
            </Typography>
            </>
            )}
          </div>
        <SkipRightIcon />
          </>

        )}
      </div>
    )
  }
  
  const handleSelectSpeciality = async (professional) => {
    const professionalType = professional.professional_type
    const isPsic = professionalType === 'PSIC'

    setProfessionalRole(professionalType)
    setLoadingEligibility(true)

    const isEnterprise = [ENTERPRISE, CARE_PLUS].includes(selectedPlan?.plan_group.category)
    const isParticular = selectedPlan.partner_plan_is_particular

    const professionalsHistoryData = await fetchProfessionalsHistory(!isParticular && planCode)

    setProfessionalHistory(professionalsHistoryData)

    const professionalsByRole = professionalsHistoryData.filter(profH => profH.profile_role === professionalType)

    const hasNoProfessionalHistory = professionalsByRole.length <= 0

    if (isPsic) {
      if(isEnterprise) {
        await getLastValidityDetail(username, planCode)
          .then(() => {
            setLoadingEligibility(false)

            if (hasNoProfessionalHistory) {
              return patientHasReservation 
                ? setBlockScheduleModalOpen(true) 
                : handleProfessionalProfileRoute(professional)
            } 
            
            setFloatButtonTab('SELECT_PROFESSIONAL')
          })
          .catch((error) => {
            setLoadingEligibility(false)

            if (error === 404) {
              setDropdown(true)
              setRequestTreatmentModalOpen(true)
              return
            }
          })
      } else {
        setLoadingEligibility(false)

        if (hasNoProfessionalHistory) {
          return patientHasReservation
            ? setBlockScheduleModalOpen(true) 
            : handleProfessionalProfileRoute(professional)
        } 
          

        setFloatButtonTab('SELECT_PROFESSIONAL')
      } 
    } 

    setLoadingEligibility(false)

    if (hasNoProfessionalHistory) {
      return handleProfessionalProfileRoute(professional)
    }

    setFloatButtonTab('SELECT_PROFESSIONAL')
  }

  const handleSelectProfessional = (professional) => {
    const role = professional?.profile_role || professional?.professional_type
    const planCode = selectedPlan?.partner_plan_code
    const particularPlanCode = 'PLN01000'
    const professionalToScheduleUsername = professional?.username
    const isSchedulingWithSameProfessional = reservationProfessionalUsername === professionalToScheduleUsername
    const isPsychologist = role === 'PSIC'
    const isPsychiatrist = role === 'PSIQ'

    if (isPsychiatrist || planCode === particularPlanCode) {
      return handleProfessionalProfileRoute(professional)
    }

    if (isPsychologist && patientHasReservation && isSchedulingWithSameProfessional) {
      return handleProfessionalProfileRoute(professional)
    }

    if (isPsychologist && patientHasReservation && !isSchedulingWithSameProfessional) {
      return setBlockScheduleModalOpen(true)
    }

    return handleProfessionalProfileRoute(professional)
  }

  const fetchProfessionalsHistory = async (planCode = null) => {
    const params = {
      status_code: [SCHEDULED, ATTENDED],
      ...(planCode && {plan_code: planCode})
    }
    try {
      const resp = await axios.get(apiEndpoints.PROFESSIONAL_HISTORY, {
        params,
        paramsSerializer: params => {
          return qs.stringify(params)
        },
      })
      const history = resp.data.professionals
      return history
    } catch (err) {
      return []
    }
  }

  const handleSelectPlan = async (plan) => {
    setLoadingHistory(true)
    setPlanCode(plan.planCode)
    setPlanGroupCode(plan.planGroupCode)
    updatePlanGroupCode(plan.planGroupCode)
    handleUpdateSelectedPlan(plan.planCode)
    const isEnterprise = [ENTERPRISE, CARE_PLUS, CAPITATION].includes(plan.category)
    const isCorporate = [CORPORATE_LIMITED, CORPORATE_UNLIMITED].includes(plan.category)
    const isParticularPlan = plan.isParticular
    const planCode = plan.planCode

    const professionalsHistoryData = await fetchProfessionalsHistory(!isParticularPlan && planCode)
    
    setProfessionalHistory(professionalsHistoryData)

    if (!showPsychiatry) {
      if(isEnterprise) {
        await getLastValidityDetail(username, plan.planCode)
          .then(() => {
            setLoadingHistory(false)
            if (professionalsHistoryData.length <= 0) {
              return patientHasReservation
                ? setBlockScheduleModalOpen(true)
                : handleProfessionalProfileRoute({ professional_type: 'PSIC' }, plan.planCode)
            }
            setFloatButtonTab('SELECT_PROFESSIONAL')
            return
          })
          .catch((error) => {
            setLoadingHistory(false)
            if (error === 404) {
              setDropdown(true)
              setProfessionalRole('PSIC')
              setRequestTreatmentModalOpen(true)
              return
            }
          })
      } else if (isCorporate) {
        if (professionalsHistoryData.length <= 0) {
          setLoadingHistory(false)
          return patientHasReservation 
            ? setBlockScheduleModalOpen(true) 
            : handleProfessionalProfileRoute({ professional_type: 'PSIC' }, plan.planCode)
        }
        setLoadingHistory(false)
        setFloatButtonTab('SELECT_PROFESSIONAL')
        return
      } else {
        if (professionalsHistoryData.length <= 0) return handleProfessionalProfileRoute({
          professional_type: 'PSIC',
        }, plan.planCode)

        setLoadingHistory(false)
        setFloatButtonTab('SELECT_PROFESSIONAL')
      }
    } else {
      setFloatButtonTab('SELECT_SPECIALTY')
    }

    setLoadingHistory(false)

  }

  const handleCloseFloatButton = () => {
    setFloatButtonTab(showPlanSelectTab ? 'SELECT_PLAN' : 'SELECT_SPECIALTY')
    setDropdown(false)
  }

  const FLOAT_MENU_DATA = {
    'SELECT_PLAN': {
      options: orderedPlansOptions,
      component: renderPlanButton,
      onClick: (data) => handleSelectPlan(data),
    },
    'SELECT_SPECIALTY': {
      options: specialitiesOptions,
      component: renderSpecialtyButton,
      onClick: (data) => handleSelectSpeciality(data),
      goBack: () => {
        setFloatButtonTab('SELECT_PLAN')
      }
    },
    'SELECT_PROFESSIONAL': {
      options: professionalsOptions,
      component: renderProfessionalChipButton,
      onClick: (data) => handleSelectProfessional(data),
      goBack: () => {
        if (specialities.length > 1) return setFloatButtonTab('SELECT_SPECIALTY')
        setFloatButtonTab('SELECT_PLAN')
      }
    },
  }

  const renderLeftButtonRipple = () => {
    if (floatButtonTab === 'SELECT_PLAN') {
      return (
        <div>
          <Typography variant='content2' weight='bold'>
          Escolha abaixo:
          </Typography>
        </div>
      )
    }

    if ((showPlanSelectTab && floatButtonTab === 'SELECT_SPECIALTY') || floatButtonTab === 'SELECT_PROFESSIONAL') {
      return (
        <Button onClick={FLOAT_MENU_DATA[floatButtonTab].goBack} variant='text' uppercase={false} customClassName='PatientHome__FloatActionButtonDropdown__BackButton'>
          <ArrowLeftIcon
            height={24}
            width={24}
            fill='#1F35B5'
          />
          <Typography variant='content2' weight='bold' color='primary'>
          Voltar
          </Typography>
        </Button>
      )
    }
  }

  useEffect(() => {
    updatePlanGroupCode(PARTICULAR_PLAN_GROUP_CODE)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  function handleCloseBlockScheduleModal() {
    setDropdown(true)
    setBlockScheduleModalOpen(false)
  }

  return (
    <>
      <FloatActionButtonDropdown
        leftButtonRipple={renderLeftButtonRipple()}
        onClose={handleCloseFloatButton}
        icon="PencilEdit"
        title={'Marcar consulta'}
        type="primary"
        options={FLOAT_MENU_DATA[floatButtonTab].options}
        renderOption={FLOAT_MENU_DATA[floatButtonTab].component}
        closeModalWhenSelectOption={closeDropdown}
        onSelectOption={FLOAT_MENU_DATA[floatButtonTab].onClick}
        updateModalState={closeDropdown}
        hasTitleOnMobile
      />
      {requestTreatmentModalOpen && (
        <RequestTreatmentModal
          onSuccess={onSuccess}
          isCareplus={isCareplus}
          isCassi={isCassi}
          isCapitation={isCapitation}
          professionalRole={professionalRole}
          onClose={() => setRequestTreatmentModalOpen(false)}
          onRequestTreatement={handleRequestTreatementStatus}
          cardNumber={selectedPlan?.card_number}
          daysToAuthorizeProcedure={daysToAuthorizeProcedure}
          planGroupCode={planGroupCode}
          planCode={planCode}
          userName={username}
        />
      )}

      {blockScheduleModalOpen && patientHasReservation && (
        <BlockScheduleModal
          buttonText='Entendi'
          hasCloseButton
          onClose={handleCloseBlockScheduleModal}
          buttonAction={handleCloseBlockScheduleModal}
        />
      )}
    </>
  )
}
